// src/modules/admin/RolesManagement.js
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { roleService } from '../../services/roleService';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { mapSupabaseError } from '../../utils/supabaseErrors';

export function RolesManagement() {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [expandedRole, setExpandedRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Set of `${roleId}:${permissionCode}` for toggles currently in flight.
  // Rapid clicks on the same checkbox are ignored (the input is disabled
  // while its key is in the set); clicks on different checkboxes work
  // independently.
  const [pendingToggles, setPendingToggles] = useState(new Set());

  const loadData = async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    try {
      setLoading(true);
      setError(null);
      const [rolesData, permissionsData] = await Promise.all([
        roleService.getAllRoles(),
        roleService.getAllPermissions(),
      ]);
      if (isCancelled()) return;
      setRoles(rolesData);
      setAllPermissions(permissionsData);
    } catch (err) {
      if (isCancelled()) return;
      setError('Failed to load roles: ' + mapSupabaseError(err));
      console.error(err);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  useAbortableFetch(loadData, []);

  const handlePermissionToggle = async (roleId, permission, isChecked) => {
    const key = `${roleId}:${permission.code}`;
    if (pendingToggles.has(key)) return; // in-flight guard

    // Capture the pre-toggle state for rollback.
    const previousRoles = roles;

    // Optimistic update: flip the permission locally now.
    setRoles((prev) =>
      prev.map((r) => {
        if (r.id !== roleId) return r;
        if (isChecked) {
          return r.permissions.some((p) => p.code === permission.code)
            ? r
            : { ...r, permissions: [...r.permissions, permission] };
        }
        return { ...r, permissions: r.permissions.filter((p) => p.code !== permission.code) };
      })
    );
    setPendingToggles((prev) => new Set(prev).add(key));

    try {
      if (isChecked) {
        await roleService.grantPermissionToRole(roleId, permission.code);
      } else {
        await roleService.revokePermissionFromRole(roleId, permission.code);
      }
    } catch (err) {
      // Revert and surface via toast — there's no form-level banner near
      // each checkbox, so the inline error UI doesn't fit here.
      setRoles(previousRoles);
      toast.error('Failed to update permission: ' + mapSupabaseError(err));
      console.error(err);
    } finally {
      setPendingToggles((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  if (loading) return <div className="admin-panel">Loading roles...</div>;

  return (
    <div className="roles-management">
      <h2>Roles & Permissions</h2>
      {error && <div className="error-message" role="alert">{error}</div>}

      {roles.map((role) => (
        <div key={role.id} className="role-section">
          <button
            className="role-header"
            onClick={() => setExpandedRole(expandedRole === role.id ? null : role.id)}
          >
            <h3>{role.name}</h3>
            <p className="role-description">{role.description}</p>
            <span className="toggle-icon">{expandedRole === role.id ? '▼' : '▶'}</span>
          </button>

          {expandedRole === role.id && (
            <div className="role-permissions">
              {['Admin Panel', 'Frontend'].map((category) => (
                <div key={category} className="permission-category">
                  <h4>{category}</h4>
                  {allPermissions
                    .filter((p) => p.category === category)
                    .map((permission) => {
                      const key = `${role.id}:${permission.code}`;
                      return (
                        <label key={permission.id} className="permission-item">
                          <input
                            type="checkbox"
                            checked={role.permissions.some(p => p.code === permission.code)}
                            onChange={(e) => handlePermissionToggle(role.id, permission, e.target.checked)}
                            disabled={role.name === 'Admin' || pendingToggles.has(key)}
                          />
                          <span>
                            <strong>{permission.name}</strong>
                            <p>{permission.description}</p>
                          </span>
                        </label>
                      );
                    })}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
